import React from 'react';
import {
  ArrowDownTrayIcon,
  CheckCircleIcon,
  DevicePhoneMobileIcon,
  BellIcon,
  BoltIcon,
} from '@heroicons/react/24/outline';
import { usePWA } from '../../hooks/usePWA';
import { useTranslation } from 'react-i18next';

export const InstallSettings: React.FC = () => {
  const { t } = useTranslation();
  const { isInstalled, isInstallable, installPrompt, isIOS, isAndroid, isStandalone } = usePWA();

  return (
    <div className="bg-gray-800 rounded-xl p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center ${
              isInstalled ? 'bg-green-500/20' : 'bg-purple-600/20'
            }`}
          >
            {isInstalled ? (
              <CheckCircleIcon className="w-5 h-5 text-green-400" />
            ) : (
              <ArrowDownTrayIcon className="w-5 h-5 text-purple-400" />
            )}
          </div>
          <div>
            <h3 className="text-dark-text font-semibold">
              {t('pwa.settings.title', '{{brandName}} App')}
            </h3>
            <p className="text-gray-400 text-sm">
              {isInstalled
                ? t('pwa.settings.installed', 'Installed on this device')
                : t('pwa.settings.notInstalled', 'Not installed')}
            </p>
          </div>
        </div>

        {!isInstalled && isInstallable && (
          <button
            onClick={installPrompt}
            className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            {isIOS
              ? t('pwa.settings.showInstructions', 'How to Install')
              : t('pwa.settings.install', 'Install')}
          </button>
        )}
      </div>

      {/* Benefits */}
      {!isInstalled && (
        <div className="pt-4 border-t border-gray-700">
          <p className="text-gray-400 text-sm mb-3">
            {t('pwa.settings.benefits', 'App benefits:')}
          </p>
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <BellIcon className="w-4 h-4 text-purple-400" />
              <span className="text-gray-500 text-sm">
                {t('pwa.settings.benefit1', 'Your stories one tap away')}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <BoltIcon className="w-4 h-4 text-purple-400" />
              <span className="text-gray-500 text-sm">
                {t('pwa.settings.benefit2', 'Faster loading & performance')}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <DevicePhoneMobileIcon className="w-4 h-4 text-purple-400" />
              <span className="text-gray-500 text-sm">
                {t('pwa.settings.benefit3', 'Home screen access')}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Platform Info */}
      {!isInstalled && !isInstallable && (
        <div className="pt-4 border-t border-gray-700">
          <p className="text-yellow-400 text-sm">
            {isIOS && t('pwa.settings.iosRequirement', 'Requires iOS 16.4+ and Safari browser')}
            {!isIOS &&
              !isAndroid &&
              t('pwa.settings.desktopInfo', 'Open in Chrome or Edge to install')}
          </p>
        </div>
      )}
    </div>
  );
};
